<template>
    <div class="q-gutter-y-md">
        <div class="row q-col-gutter-md">
            <div class="col-12 col-md-4">
                <q-input
                    v-model="filter.search"
                    label="Search Calendar or Congregation"
                    dense
                    outlined
                    debounce="300"
                    clearable
                >
                    <template v-slot:append>
                        <q-icon name="search" />
                    </template>
                </q-input>
            </div>
            <div class="col-12 col-md-4 flex items-center">
                <q-checkbox
                    v-model="filter.activeLast6Months"
                    label="Active in last 6 months"
                />
            </div>
        </div>

        <q-table
            class="sticky-container-table"
            title="Calendars"
            :rows="rows"
            :columns="columns"
            v-model:pagination="pagination"
            :loading="loading"
            @request="onRequest"
            binary-state-sort
            row-key="id"
            flat
            bordered
        >
            <template v-slot:body-cell-name="props">
                <q-td :props="props">
                    <a
                        :href="'/dev/calendar/' + props.row.id"
                        class="text-primary text-weight-bold"
                    >
                        {{ props.row.name }}
                    </a>
                </q-td>
            </template>
            <template v-slot:body-cell-latestActivity="props">
                <q-td :props="props">
                    <div v-if="props.row.latestActivity">
                        {{ props.row.latestActivity }}
                        <div class="text-grey text-caption">
                            {{ props.row.latestActivityHuman }}
                        </div>
                    </div>
                    <span v-else class="text-grey">Never</span>
                </q-td>
            </template>
            <template v-slot:body-cell-actions="props">
                <q-td :props="props" class="text-right">
                    <q-btn
                        flat
                        dense
                        color="primary"
                        icon="visibility"
                        :href="'/dev/calendar/' + props.row.id"
                    >
                        <q-tooltip>Show Details</q-tooltip>
                    </q-btn>
                    <q-btn
                        flat
                        dense
                        color="negative"
                        icon="delete"
                        :href="'/dev/calendar/' + props.row.id + '/delete'"
                    >
                        <q-tooltip>Delete Calendar</q-tooltip>
                    </q-btn>
                </q-td>
            </template>
        </q-table>
    </div>
</template>

<script>
import { ref, onMounted, watch } from "vue";
import axios from "axios";
import { useQuasar } from "quasar";

export default {
    setup() {
        const $q = useQuasar();
        const rows = ref([]);
        const loading = ref(false);
        const pagination = ref({
            sortBy: "latestActivity",
            descending: true,
            page: 1,
            rowsPerPage: 15,
            rowsNumber: 0,
        });

        const filter = ref({
            search: "",
            activeLast6Months: false,
        });

        const columns = [
            {
                name: "id",
                label: "ID",
                field: "id",
                sortable: true,
                align: "left",
            },
            {
                name: "name",
                label: "Name",
                field: "name",
                sortable: true,
                align: "left",
            },
            {
                name: "publisherCount",
                label: "Publishers",
                field: "publisherCount",
                sortable: true,
                align: "center",
            },
            {
                name: "latestActivity",
                label: "Latest Activity",
                field: "latestActivity",
                sortable: true,
                align: "left",
            },
            {
                name: "actions",
                label: "Actions",
                field: "actions",
                align: "right",
            },
        ];

        const fetchFromServer = (
            start,
            limit,
            sortBy,
            descending,
            filterValues,
        ) => {
            const params = {
                start,
                limit,
                sortBy,
                descending,
                ...filterValues,
            };
            return axios.get("/dev/api/calendars", { params });
        };

        const onRequest = (props) => {
            const { page, rowsPerPage, sortBy, descending } = props.pagination;
            const filterValues = filter.value;

            loading.value = true;

            const start = (page - 1) * rowsPerPage;
            const limit = rowsPerPage;

            fetchFromServer(start, limit, sortBy, descending, filterValues)
                .then((response) => {
                    rows.value = response.data.rows;
                    pagination.value.rowsNumber = response.data.rowsNumber;

                    pagination.value.page = page;
                    pagination.value.rowsPerPage = rowsPerPage;
                    pagination.value.sortBy = sortBy;
                    pagination.value.descending = descending;

                    loading.value = false;
                })
                .catch((error) => {
                    loading.value = false;
                    $q.notify({
                        color: "negative",
                        message: "Failed to fetch calendars",
                        icon: "report_problem",
                    });
                });
        };

        // Watch filters to trigger reload
        watch(
            filter,
            () => {
                pagination.value.page = 1;
                onRequest({ pagination: pagination.value });
            },
            { deep: true },
        );

        onMounted(() => {
            onRequest({ pagination: pagination.value });
        });

        return {
            rows,
            columns,
            loading,
            pagination,
            filter,
            onRequest,
        };
    },
};
</script>
